import moment from 'moment';
import React, { SetStateAction } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TiWarningOutline } from 'react-icons/ti';
import { Campaign } from '../../../../model/campaign/Campaign';
import { IOfferItems } from '../../../../model/offer/IOfferItems';
import { User } from '../../../../model/user/User';
import { CustomImageHandler } from '../../../../shared/components/CustomImageHandler';
import { formatDate } from '../../../../shared/functions/Functions';
import { AgencyPlannerService } from '../../../agency/planner/service';
import { iManageList } from '../../../brand/campaign/manage/calendar/dto/IManageCalendar';
import { INewEvent } from '../../planner/components/CreateEventModal';
import { InfluencerPlannerService } from '../../planner/service';
import CardLayout from '../../../../shared/components/card/CardLayout';
import { PlannerStatusEnum } from '../../../../enum/PlannerStatusEnum';
import TooltipOverlay from '../../../../shared/components/tooltip/Tooltip';

export interface ISelectedCampaign {
    campaign: Campaign;
    contentCreatorId?: number;
    offerItemId: number;
}

interface IReportingCampaignCardProps {
    offerItem: IOfferItems;
    setSelectedIdOffer: React.Dispatch<SetStateAction<number>>;
    isReviewed: boolean;
    offerStatus?: string;
    agencyUserId?: number;
    acceptedUser?: User;
    campaign: Campaign;
    setCreateEvent: React.Dispatch<SetStateAction<INewEvent>>;
    setSelectedCampaign: React.Dispatch<SetStateAction<ISelectedCampaign | undefined>>;
    setIsNewReporting: React.Dispatch<SetStateAction<boolean>>;
    setIsCreateModalOpened: React.Dispatch<SetStateAction<boolean>>;
    setIsProposalOpened: React.Dispatch<SetStateAction<boolean>>;
    setIsAdditional: React.Dispatch<SetStateAction<boolean>>;
    setIsPreviewEventOpened: React.Dispatch<SetStateAction<boolean>>;
    setSelectedEvent: React.Dispatch<SetStateAction<iManageList | undefined>>;
}

const getCorrectReportIcon = (isVerified?: boolean, isRejected?: boolean) => {
    if (isRejected) {
        return 'not-approved-icon.svg';
    } else if (isVerified) {
        return 'approved-icon.svg';
    } else {
        return 'being-verified-icon.svg';
    }
};

export interface ISelectedEvent {
    event: iManageList;
    campaign: Campaign;
    offerItem: IOfferItems;
}

export interface IReportingUpdate {
    campaignId?: number;
    offerItemId?: number;
    socialPlatformId?: number;
    eventId?: number;
    postingDate?: string;
    externalLinks?: string[];
    postingLink?: string;
    clicks: number;
    comments: number;
    likes: number;
    reach: number;
    saves: number;
    shares: number;
    stickerEngagements: number;
    swipeUpLinks: number;
    views: number;
    watchTime: number;
    file?: Blob[] | MediaSource[];
    attachments?: Blob[] | MediaSource[];
}

export const dueStatuses = ['published', 'approved', 'reported'];
export const plannedStatuses = ['scheduled', 'feedback_loop', 'proposed'];

const ReportingCampaignCard = ({
    acceptedUser,
    offerItem,
    offerStatus,
    agencyUserId,
    setCreateEvent,
    isReviewed,
    setIsCreateModalOpened,
    setIsProposalOpened,
    campaign,
    setSelectedIdOffer,
    setSelectedEvent,
    setIsPreviewEventOpened,
    setSelectedCampaign,
    setIsNewReporting,
    setIsAdditional,
}: IReportingCampaignCardProps) => {
    const { t } = useTranslation();
    const { socialPlatform, offerItemEvents } = offerItem;

    const getCorrectButtonName = (status: string) => {
        if (['published', 'approved'].includes(status)) {
            return t(`influencer.reporting.report`);
        }
        if (status === 'reported') {
            return 'edit';
        }
        if (!status) {
            return 'plan';
        }
        return 'edit';
    };

    const getCorrectPlaceholderImg = (status: string) => {
        if (!status) {
            return '/icons/placeholders/add.svg';
        }
        return '/icons/placeholders/processing.svg';
    };

    const handleClickReporting = async (status: string, eventItem: iManageList) => {
        setIsAdditional(false);
        if (offerStatus === 'completed' && eventItem.status !== 'reported') {
            setSelectedCampaign({ campaign, offerItemId: offerItem?.id, contentCreatorId: agencyUserId });
            setIsNewReporting(true);
        } else if (!status && isReviewed) {
            // empty card & feedback enabled
            setIsCreateModalOpened(true);
            setCreateEvent(prevState => ({
                ...prevState,
                acceptedUser,
                postingDate: moment(campaign?.publishFrom).add(1, 'day').toISOString(),
                campaign: campaign,
                userId: agencyUserId,
                offerItemId: offerItem?.id,
            }));
        } else if (!status && !isReviewed) {
            // empty card & feedback disabled
            setSelectedCampaign({
                campaign,
                offerItemId: offerItem?.id,
                contentCreatorId: agencyUserId,
            });
            setIsNewReporting(true);
        } else if (status === 'proposed' && isReviewed) {
            // proposal event open proposal modal if feedback loop enabled
            const data = !!agencyUserId
                ? await AgencyPlannerService.getPlannerModalData(String(eventItem?.id))
                : await InfluencerPlannerService.getPlannerModalData(String(eventItem?.id));
            setIsProposalOpened(true);
            setSelectedEvent(data);
        } else if (status && !dueStatuses.includes(status) && isReviewed && status !== 'proposed') {
            // existing card & feedback enabled point to planner
            const data = await InfluencerPlannerService.getPlannerModalData(String(eventItem?.id));
            setIsPreviewEventOpened(true);
            setSelectedEvent(data);
        } else if ((status && !isReviewed) || dueStatuses.includes(status)) {
            // existing card  & feedback disabled
            setSelectedIdOffer(eventItem?.id);
        }
    };
    return (
        <>
            {offerItemEvents?.map((eventItem, index) => {
                const { status, date, attachments, id, isRejected, isVerified } = eventItem;
                const hasPicture = !!attachments?.[0]?.path || !!attachments?.[0]?.thumbnail;
                return (
                    <div
                        className="reporting-card col-12 col-sm-6 col-lg-4 align-self-stretch mt-3 default-radius px-2"
                        key={`${campaign?.id}-${id || index}-${offerItem?.id}`}
                    >
                        <CardLayout
                            classBody="p-0"
                            classCard={isRejected ? 'border border-danger overflow-hidden' : ''}
                        >
                            <div className="status-position">
                                {status && (
                                    <p className={`status ${status} my-1 my-md-0`}>
                                        {t(`brand.campaign.manage.${status}`)}
                                    </p>
                                )}
                            </div>
                            {status === PlannerStatusEnum.REPORTED && (
                                <TooltipOverlay
                                    className="position-absolute z-index-1 cursor-pointer"
                                    style={{ top: '1rem', right: '1rem' }}
                                    content={
                                        isVerified
                                            ? t('brand.campaign.measure.verified')
                                            : isRejected
                                            ? t('general.campaign_status.rejected')
                                            : t('brand.campaign.measure.being_verified')
                                    }
                                >
                                    <img
                                        src={`/icons/${getCorrectReportIcon(isVerified, isRejected)}`}
                                        alt="Report icon"
                                    />
                                </TooltipOverlay>
                            )}
                            <div className="reporting-img-container">
                                <CustomImageHandler
                                    classes={` ${hasPicture && 'w-100 h-100'}`}
                                    thumbnailPath={attachments?.[0]?.thumbnail}
                                    onClick={() => handleClickReporting(status, eventItem)}
                                    photoPath={
                                        !!attachments?.[0]?.thumbnail
                                            ? (attachments?.[0].thumbPath as string)
                                            : (attachments?.[0]?.path as string)
                                    }
                                    altTag="Campaign photo"
                                    placeholder={getCorrectPlaceholderImg(status)}
                                />
                                {[PlannerStatusEnum.SCHEDULED, PlannerStatusEnum.FEEDBACK_LOOP].includes(
                                    status as PlannerStatusEnum
                                ) &&
                                    isReviewed && (
                                        <p
                                            className={`px-3 mt-1 mb-0 text-danger text-center warning-label ${
                                                hasPicture && 'shadow'
                                            }`}
                                        >
                                            <span className="me-1">
                                                <TiWarningOutline className="mb-1" size={14} />
                                            </span>
                                            {t('general.waitingForApproval')}
                                        </p>
                                    )}
                                {!status && (
                                    <p className="mt-1 mb-0 text-warning text-center px-3 warning-label">
                                        {t(`influencer.reporting.scheduling_miss`)}
                                    </p>
                                )}
                            </div>
                            <div className="p-2 d-flex justify-content-between">
                                <div className="d-flex align-items-center">
                                    <img src={socialPlatform?.path} alt="Platform icon" />
                                    <div className="ml-1">
                                        <p className="mb-0">
                                            {t(`general.platforms.${socialPlatform?.name}`)}{' '}
                                            {eventItem?.platformPosition ?? '-'}/{offerItem?.nbPosts}
                                        </p>
                                        {date && <p className="text-muted mb-0">{formatDate(date)}</p>}
                                    </div>
                                </div>

                                <Button
                                    variant={
                                        plannedStatuses?.includes(status) || status === 'reported'
                                            ? 'outline-primary'
                                            : 'primary'
                                    }
                                    className={status === 'approved' ? 'px-2' : ''}
                                    size="sm"
                                    onClick={() => handleClickReporting(status, eventItem)}
                                >
                                    {getCorrectButtonName(status)}
                                </Button>
                            </div>
                        </CardLayout>
                    </div>
                );
            })}
        </>
    );
};

export default ReportingCampaignCard;
